import React, { useState } from "react";
import api from "@/lib/api";
import { uploadToCloudinary } from "@/lib/cloudinary";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Camera, X as IconX, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/** Dialog para que el montador suba fotos + comentario y solicite OK del admin.
 *  Se invoca desde el panel de líneas con `requiere_validacion`.
 *  Props: { open, onClose, linea, onSubmitted, iteracion }
 *  iteracion > 1 indica que se está re-subiendo tras un rechazo.
 */
export default function SolicitarValidacionDialog({ open, onClose, linea, onSubmitted, iteracion = 1 }) {
  const [fotos, setFotos] = useState([]);  // [{file, preview, uploading, url, public_id, comentario}]
  const [comentario, setComentario] = useState("");
  const [saving, setSaving] = useState(false);

  const addFiles = async (files) => {
    const arr = Array.from(files).slice(0, 10 - fotos.length);
    const previews = arr.map((f) => ({
      file: f, preview: URL.createObjectURL(f),
      uploading: true, url: null, public_id: null, comentario: "",
    }));
    setFotos((prev) => [...prev, ...previews]);

    // Sube en paralelo a Cloudinary
    for (let i = 0; i < arr.length; i++) {
      const idx = fotos.length + i;
      try {
        const up = await uploadToCloudinary(arr[i], "dnf/validaciones-montaje", "image");
        setFotos((prev) => prev.map((p, j) => j === idx
          ? { ...p, uploading: false, url: up.secure_url, public_id: up.public_id }
          : p));
      } catch (e) {
        setFotos((prev) => prev.map((p, j) => j === idx
          ? { ...p, uploading: false, url: null, error: e.message }
          : p));
        toast.error(`Error subiendo ${arr[i].name}`);
      }
    }
  };

  const removeFoto = (idx) => setFotos((prev) => prev.filter((_, i) => i !== idx));
  const updateFotoComentario = (idx, v) => setFotos((prev) =>
    prev.map((p, i) => i === idx ? { ...p, comentario: v } : p));

  const submit = async () => {
    const validFotos = fotos.filter((f) => f.url && !f.uploading);
    if (validFotos.length === 0) {
      toast.error("Sube al menos 1 foto");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/lineas/${linea.id}/solicitar-validacion`, {
        fotos: validFotos.map((f) => ({
          url: f.url, public_id: f.public_id, comentario: f.comentario || null,
        })),
        comentario: comentario || null,
      });
      toast.success("✅ Solicitud enviada. Esperando OK del admin.");
      setFotos([]); setComentario("");
      onSubmitted?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error solicitando validación");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="solicitar-validacion-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-cyan-400" />
            {iteracion > 1
              ? `Reenviar para nuevo OK (intento #${iteracion})`
              : "Solicitar OK del admin"}
          </DialogTitle>
        </DialogHeader>

        <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-3 text-xs text-cyan-200">
          🛡 Sube fotos del montaje completado y comenta cualquier detalle. Un admin recibirá la notificación
          y dará el OK o pedirá ajustes.
        </div>

        {iteracion > 1 && (
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-xs text-orange-200">
            ⚠️ Esta solicitud anterior fue rechazada. Adjunta fotos del trabajo corregido.
          </div>
        )}

        {/* Uploader */}
        <div>
          <Label className="text-xs text-zinc-400">Fotos del montaje *</Label>
          <label className="mt-1 block border-2 border-dashed border-zinc-700 hover:border-cyan-500/50 rounded-lg p-6 text-center cursor-pointer transition-colors"
            data-testid="validacion-upload-area">
            <input type="file" accept="image/*" multiple capture="environment" className="hidden"
              onChange={(e) => e.target.files && addFiles(e.target.files)}
              data-testid="validacion-file-input" />
            <Camera className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
            <div className="text-sm text-zinc-300">Click para subir o hacer foto</div>
            <div className="text-[11px] text-zinc-500 mt-1">JPG/PNG · máx 10 fotos</div>
          </label>
        </div>

        {/* Grid de fotos */}
        {fotos.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
            {fotos.map((f, idx) => (
              <div key={idx} className="relative group">
                <div className="aspect-square rounded-lg bg-zinc-900 border border-zinc-800 overflow-hidden">
                  <img src={f.preview} alt="" className="w-full h-full object-cover" />
                  {f.uploading && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
                    </div>
                  )}
                  {f.url && (
                    <div className="absolute bottom-0 inset-x-0 bg-emerald-500/80 text-[10px] text-center py-0.5 text-emerald-950 font-semibold">✓ Subida</div>
                  )}
                </div>
                <button onClick={() => removeFoto(idx)}
                  className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">
                  <IconX className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div>
          <Label className="text-xs text-zinc-400">Comentario (opcional)</Label>
          <Textarea value={comentario} onChange={(e) => setComentario(e.target.value)}
            rows={3} placeholder="Detalles del montaje, incidencias menores, observaciones…"
            className="bg-zinc-900 border-zinc-800 mt-1"
            data-testid="validacion-comentario" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}
            className="bg-transparent border-zinc-700" disabled={saving}>Cancelar</Button>
          <Button onClick={submit}
            disabled={saving || fotos.length === 0 || fotos.some((f) => f.uploading)}
            className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 font-semibold"
            data-testid="btn-enviar-validacion">
            {saving
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando…</>
              : <><ShieldCheck className="w-4 h-4 mr-2" /> Solicitar OK</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
